import { DatePipe } from '@angular/common';
import { provideHttpClient, withFetch, withInterceptors, withXsrfConfiguration } from '@angular/common/http';
import {
    ApplicationConfig,
    DestroyRef,
    ErrorHandler,
    inject,
    Injector,
    isDevMode,
    provideAppInitializer,
    provideZonelessChangeDetection,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationError, provideRouter, Router, withComponentInputBinding } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { provideTransloco } from '@jsverse/transloco';
import { provideHighcharts } from 'highcharts-angular';
import { MERMAID_OPTIONS, provideMarkdown } from 'ngx-markdown';
import { filter } from 'rxjs';

import { provideAdvaniaStyles } from '@advania-cosoftware/advania-styles';
import { provideAdvaniaVersioning, VersionService } from '@advania-cosoftware/advania-versioning';
import { authRedirectInterceptor, provideAnalytics, WolfConfig } from '@advania-cosoftware/wolf-core';
import { ScriptsService } from '@advania-cosoftware/wolf-legacy';

import { environment } from '../environments/environment';
import { routes } from './app-routes';
import { DomainProfileService } from './features/environment/domain-profile.service';
import { createVersioningConfig } from './features/versioning/versioning-config';
import { TranslocoHttpLoader } from './transloco-loader';

export const appConfig: ApplicationConfig = {
    providers: [
        provideZonelessChangeDetection(),
        provideAdvaniaStyles(),
        provideAdvaniaVersioning(() => {
            const injector = inject(Injector);

            return createVersioningConfig(() => injector.get(VersionService).acceptUpdate());
        }),
        provideAppInitializer(() => {
            inject(DomainProfileService);
        }),
        provideAppInitializer(() => {
            const destroyRef = inject(DestroyRef);
            const injector = inject(Injector);
            const router = inject(Router);

            router.events
                .pipe(
                    filter((event): event is NavigationError => event instanceof NavigationError),
                    takeUntilDestroyed(destroyRef),
                )
                .subscribe((event) => {
                    if (String(event.error).includes('Failed to fetch dynamically imported module')) {
                        void injector.get(VersionService).checkNow();
                    }
                });
        }),
        provideRouter(routes, withComponentInputBinding()),
        provideServiceWorker('ngsw-worker.js', {
            enabled: true,
            registrationStrategy: 'registerWhenStable:30000',
        }),
        provideMarkdown({
            mermaidOptions: {
                provide: MERMAID_OPTIONS,
                useValue: {
                    darkMode: true,
                    look: 'classic',
                },
            },
        }),
        provideHighcharts({
            modules: () => {
                return [
                    (async () => {
                        await import('highcharts/esm/modules/accessibility');
                        await import('highcharts/esm/modules/exporting');
                        await import('highcharts/esm/highcharts-more');
                        await import('highcharts/esm/modules/solid-gauge');
                        await import('highcharts/esm/modules/drilldown');
                        await import('highcharts/esm/modules/treemap');
                        await import('highcharts/esm/modules/heatmap');
                        await import('highcharts/esm/modules/tilemap');
                        await import('highcharts/esm/modules/timeline');
                        await import('highcharts/esm/modules/gantt');

                        return await import('highcharts/esm/modules/map');
                    })(),
                ];
            },
        }),
        provideHttpClient(
            withInterceptors([authRedirectInterceptor]),
            withFetch(),
            withXsrfConfiguration({ cookieName: 'XSRF-TOKEN', headerName: 'X-XSRF-TOKEN' }),
        ),
        { provide: WolfConfig, useValue: environment },
        { provide: ErrorHandler },
        DatePipe,
        ScriptsService,
        provideTransloco({
            config: {
                availableLangs: ['en', 'no'],
                defaultLang: 'en',
                fallbackLang: 'en',
                reRenderOnLangChange: true, // Remove this option if the application does not support changing langs at run time
                prodMode: !isDevMode(),
                missingHandler: {
                    useFallbackTranslation: true,
                    logMissingKey: true,
                },
            },
            loader: TranslocoHttpLoader,
        }),
        provideAnalytics(),
    ],
};
